import { useObserver } from 'mobx-react';
import { useEffect } from 'react';
import { translate } from '../../../../../common/intl';
import { DocumentRevisionStatus } from '../../../../../state/ducks/documentRevisions/types';
import FBAutocompleteAsyncState from '../../../FBAutocompleteAsync/FBAutocompleteAsync.state';
import FBAutocompleteAsyncStore from '../../../FBAutocompleteAsync/FBAutocompleteAsync.store';
import { FBAutocompleteAsyncOption } from '../../../FBAutocompleteAsync/FBAutocompleteAsync.types';
import { FB } from '../../../helpers';
import { MaterialOption } from '../types';

interface AutocompleteOption { label: string, value: string }
interface ParsedMaterialOptions {
  materialSelectOptions: AutocompleteOption[]
  allowedEditOptions: AutocompleteOption[]
}

const mapToAutocompleteOption = (material: MaterialOption): AutocompleteOption => ({
  label: translate('common.select.revision', {
    docId: material.document.docId,
    displayRevision: material.displayRevision,
    name: material.name,
  }),
  value: material.id,
});

// The materials catalog is document-wide and identical for every materials
// TreeList on the page. Parsing it is two full `.map()` passes over the whole
// catalog; without this cache each of N tables redid both on every render.
// Keyed on the store Map identity + size + edit-status set so it recomputes
// only when the data (or the caller's allowed-status filter) actually changes.
let optionsCache: { map: unknown, size: number, statuses: string, value: ParsedMaterialOptions } | undefined;

const parseMaterialOptions = (
  materialMap: Map<string, MaterialOption> | undefined,
  size: number,
  editAllowedStatuses: DocumentRevisionStatus[],
): ParsedMaterialOptions => {
  const statuses = editAllowedStatuses.join(',');
  if (
    optionsCache
    && optionsCache.map === materialMap
    && optionsCache.size === size
    && optionsCache.statuses === statuses
  ) {
    return optionsCache.value;
  }

  const materials = Array.from(materialMap?.values() ?? []);
  const value: ParsedMaterialOptions = {
    materialSelectOptions: materials.map(mapToAutocompleteOption),
    allowedEditOptions: materials
      .filter((material) => editAllowedStatuses.includes(material.status))
      .map(mapToAutocompleteOption),
  };

  optionsCache = { map: materialMap, size, statuses, value };
  return value;
};

export const useFetchData = ({ isEBProcedure }: { isEBProcedure?: boolean }): ParsedMaterialOptions => {
  const materialState = FB.useRef<FBAutocompleteAsyncState>(FBAutocompleteAsyncState, {
    optionId: FBAutocompleteAsyncOption.materials,
  });

  useEffect(() => {
    materialState.load();
  }, [materialState]);

  const editAllowedStatuses = isEBProcedure
    ? [DocumentRevisionStatus.Released, DocumentRevisionStatus.Draft]
    : [DocumentRevisionStatus.Released];

  return useObserver<ParsedMaterialOptions>(() => {
    const materialMap = FBAutocompleteAsyncStore.data.get(FBAutocompleteAsyncOption.materials) as Map<string, MaterialOption> | undefined;
    // Read size inside the observer so it still re-runs when entries are
    // added/removed, not only when the whole Map is replaced.
    return parseMaterialOptions(materialMap, materialMap?.size ?? 0, editAllowedStatuses);
  });
};
